/**
 * @file: vin-info.service.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { HttpClient } from '@euler/lib/request';
import {
  ExistingVehicleInfo,
  MediaWithSource,
  VehicleInfoCore,
  VehicleServiceManifest,
  VehicleServiceRecord,
  VehicleServiceRecordProjection,
} from '@euler/model';
import { VehicleInfo } from '@euler/model/entity';
import { ResourceAccessScope } from '@euler/model/enum';
import { MediaAlbum, MediaAlbumByYearMonth } from '@euler/model/MediaAlbum';
import { PendingIssueWithDetails } from '@euler/model/PendingIssueWithDetails';
import { MediaSourceType } from '@euler/model/report';
import {
  VehicleBrandInfo,
  VehicleModelInfo,
  VehicleSubscriptionDetailInfo,
  VehicleSubscriptionInfo,
} from '@euler/model/vehicle';
import { DecodedVinInfo } from '@euler/model/vehicle/VinInfo';
import { formatDateTime } from '@euler/utils/formatters';

export type VehicleServiceInfo = {
  vehicleInfo: VehicleInfoCore;
  serviceRecords: VehicleServiceRecord[];
  hasObservers: boolean;
};

export type VehicleMediaSearchParams = {
  licensePlateNo: string;
  vin: string;
  mileage?: number;
  year?: number;
  month?: number;
  sources?: MediaSourceType[];
  scope?: ResourceAccessScope;
};

export class VehicleInfoService {
  constructor(private readonly api: HttpClient) {}

  async getPendingIssues(args: {
    vin: string;
    licensePlateNo?: string;
    scope?: ResourceAccessScope;
  }): Promise<PendingIssueWithDetails[]> {
    return await this.api.get().url('/vehicle-info/issues', args).future();
  }

  async getVehicleBrands(): Promise<VehicleBrandInfo[]> {
    return await this.api.get().url('/vehicle-info/brands').future();
  }

  async getVehicleModelsByBrand(brand: string): Promise<VehicleModelInfo[]> {
    return await this.api.get().url('/vehicle-info/models', { brand }).future();
  }

  async getVinInfo(
    vin: string,
    options?: {
      refresh?: boolean;
    },
  ): Promise<{
    vinInfo: VehicleInfo;
    decodedVinInfo?: DecodedVinInfo | null;
  }> {
    return await this.api
      .get()
      .url('/vehicle-info/vin-info', { vin, ...options })
      .future();
  }

  async listVehicleServiceInfo(options: {
    scope: ResourceAccessScope;
    projection?: Partial<VehicleServiceRecordProjection>;
    plateno?: string;
    vin?: string;
    task_no?: string;
    start_date?: string | Date;
    end_date?: string | Date;
    offset?: number;
    limit?: number;
  }): Promise<VehicleServiceInfo | null> {
    return await this.api
      .get()
      .url('/vehicle-info/service-records', {
        ...options,
        start_date: options.start_date
          ? formatDateTime(options.start_date, 'yyyy-MM-dd HH:mm:ss')
          : undefined,
        end_date: options.end_date
          ? formatDateTime(options.end_date, 'yyyy-MM-dd HH:mm:ss')
          : undefined,
        projection: options.projection
          ? Object.entries(options.projection)
              .filter(([, v]) => v)
              .map(([k]) => k)
              .join(',')
          : undefined,
      })
      .future();
  }

  async queryVehicleServiceManifest(
    vin: string,
    options?: {
      task_no?: string;
      start_date?: string | Date;
      end_date?: string | Date;
    },
  ): Promise<VehicleServiceManifest> {
    return await this.api
      .get()
      .url('/vehicle-info/service-manifest', {
        vin,
        ...options,
        start_date: options?.start_date
          ? formatDateTime(options?.start_date, 'yyyy-MM-dd HH:mm:ss')
          : undefined,
        end_date: options?.end_date
          ? formatDateTime(options?.end_date, 'yyyy-MM-dd HH:mm:ss')
          : undefined,
      })
      .future();
  }

  async getMediaAlbumsByMileage(params: {
    licensePlateNo: string;
    vin: string;
    sources?: MediaSourceType[];
    scope?: ResourceAccessScope;
    includesCurrent?: boolean;
  }): Promise<MediaAlbum<number>[]> {
    return await this.api
      .get()
      .url('/vehicle-info/medias/albums/by-mileage', {
        ...params,
        sources:
          params.sources != null && params.sources.length > 0
            ? params.sources.join(',')
            : undefined,
      })
      .future();
  }

  async getMediaAlbumsByYearMonth(params: {
    licensePlateNo: string;
    vin: string;
    sources?: MediaSourceType[];
    scope?: ResourceAccessScope;
    includesCurrent?: boolean;
  }): Promise<MediaAlbumByYearMonth[]> {
    return await this.api
      .get()
      .url('/vehicle-info/medias/albums/by-year-month', {
        ...params,
        sources:
          params.sources != null && params.sources.length > 0
            ? params.sources.join(',')
            : undefined,
      })
      .future();
  }

  async searchMedias(
    params: VehicleMediaSearchParams,
  ): Promise<MediaWithSource[]> {
    return await this.api
      .get()
      .url('/vehicle-info/medias', {
        ...params,
        sources:
          params.sources != null && params.sources.length > 0
            ? params.sources.join(',')
            : undefined,
      })
      .future();
  }

  async getExistingVehicleInfo(args: {
    vin?: string;
    licensePlateNo?: string;
  }): Promise<ExistingVehicleInfo | null> {
    return await this.api
      .get()
      .url('/vehicle-info/existing-vehicle-info', args)
      .future();
  }

  async getSubscriptions(
    filter: { licensePlateNo: string; includeDetails?: boolean },
    offset?: number,
    limit?: number,
  ): Promise<VehicleSubscriptionInfo[]> {
    return await this.api
      .get()
      .url('/vehicle-info/subscriptions', { ...filter, offset, limit })
      .future();
  }

  async getSubscriptionDetails(
    subscriptionId: string,
  ): Promise<VehicleSubscriptionDetailInfo[]> {
    return await this.api
      .get()
      .url('/vehicle-info/subscriptions/:subscriptionId/details', {
        subscriptionId,
      })
      .future();
  }

  async removeSubscription(subscriptionId: string): Promise<void> {
    await this.api
      .delete()
      .url('/vehicle-info/subscriptions/:subscriptionId', {
        subscriptionId,
      })
      .future();
  }

  async getRandomRealVin(): Promise<string> {
    return await this.api.get().url('/vehicle-info/random-real-vin').future();
  }
}
